import type { PointerEvent as ReactPointerEvent } from 'react'

interface SidePanelResizeHandleProps {
  /**
   * The panel's edge it sits on: `start` (its left) for a panel on the
   * right, `end` (its right) for a panel on the left.
   */
  edge: 'start' | 'end'
  label: string
  isResizing: boolean
  onPointerDown: (event: ReactPointerEvent<HTMLDivElement>) => void
  /** Panels over the table's own layers (Record Preview, the right-hand panels) sit higher. */
  layer?: 'panel' | 'overlay'
}

/**
 * A side panel's resize handle, down the middle of the gap beside the panel
 * (--side-panel-handle-offset; see the page layout tokens). The strip that
 * takes the pointer is wider than the gap, so it is easy to catch. Hovered or
 * dragged, it shows an info-blue bar as wide as the gap and as tall as the
 * panel, rounded at both ends, like an editor's split between panes.
 */
export default function SidePanelResizeHandle({
  edge,
  label,
  isResizing,
  onPointerDown,
  layer = 'panel',
}: SidePanelResizeHandleProps) {
  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label={label}
      onPointerDown={onPointerDown}
      className={`group absolute top-0 ${layer === 'overlay' ? 'z-50' : 'z-20'} flex h-full w-3 cursor-col-resize justify-center ${
        edge === 'start'
          ? 'left-[var(--side-panel-handle-offset)] -translate-x-1/2'
          : 'right-[var(--side-panel-handle-offset)] translate-x-1/2'
      }`}
    >
      <span
        // At a gap of 0 the cards meet behind one line; the bar stays visible.
        className={`h-full w-[max(3px,var(--page-card-gap))] rounded-full transition-colors duration-100 ${
          isResizing
            ? 'bg-[color:var(--accent-info)]'
            : 'bg-transparent group-hover:bg-[color:var(--accent-info-hover)]'
        }`}
        aria-hidden="true"
      />
    </div>
  )
}
